import { h } from '@omni-torn/ui';
import {
  CRIME_MEASURES,
  CRIME_TYPES,
  crimeBySlug,
  crimeLogCsvRows,
  crimeNameIndex,
  crimeRows,
  crimeTotals,
  findLogCategory,
  inRange,
  itemsValue,
  looseName,
  MAX_CRIME_SKILL,
  rangeText,
  readCrimeCatalog,
  readCrimeLogEntry,
  readCrimesLite,
  readSubcrimes,
  readUserCrime,
  summariseCrimeLog,
  sumPerDay,
  uniquesMissing,
  type CrimeInfo,
  type CrimeLogEntry,
  type CrimeMeasure,
  type CrimeRow,
  type CrimesLite,
  type CrimeStats,
  type DateRange,
  type SubcrimeInfo,
} from '@omni-torn/shared';
import { forgetLogText, ownHistoryOn, readingLogText } from '../own-history';
import { firstRead, LogReader, type LogStream } from '../log-kit/reader';
import { logStore, type LogStore } from '../log-kit/store';
import { csvButton, filterSelect, keptLogPanel, rangeFromAddress, rangePicker, setAddressQuery, zoomChart } from '../log-kit/ui';
import { session, torn, tornCallsLeft } from '../session';
import { ago, box, facts, gated, kids, loading, money, num, problem, short, table, tabs, tct } from '../ui';
import { ref } from './torn';

/**
 * "Your crimes" (You → Your crimes, website roadmap S09 — torn.report's Crimes, Crimes Lite and Uniques pages):
 *  - All crimes: every crime's skill from personal stats (one call — "Crimes Lite"), then each crime's whole record
 *    (user/{id}/crimes, one call each, kept in this browser for an hour and read again only then): nerve, attempts and
 *    outcomes, money and items at market value, money per nerve, uniques found of the crime's total;
 *  - One crime: its sub-crimes, the items it gave, its uniques (found, with their rewards; Torn gives the rest as numbers
 *    only), its own counters;
 *  - From your log (Full key): the log kit — your log kept in this browser (IndexedDB, only here), any date range (also
 *    in the page address), a chart you zoom by dragging, filters, per-crime sums with profit after costs, CSV.
 * Your own key only; nothing new goes to the Omni-Torn server (its 90-day log copy, when tracking is on, fills in).
 */

type Tab = 'all' | 'crime' | 'log';

interface Kept {
  stats: Record<number, CrimeStats>;
  lite: CrimesLite | null;
  liteAt: number;
}

interface Ref {
  at: number;
  catalog: CrimeInfo[];
  subs: Record<number, SubcrimeInfo[]>;
  logCat: number | null;
  logCatAt: number;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const keptKey = (p: number) => `omni-torn.crimes.${p}`;
const REF_KEY = 'omni-torn.crimes-ref';
/** Calls left for other pages when reading every crime. */
const SPARE = 5;

function load<T>(k: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(k);
    return raw ? ({ ...fallback, ...(JSON.parse(raw) as T) } as T) : fallback;
  } catch {
    return fallback;
  }
}
function save(k: string, v: unknown): void {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {
    /* storage full or blocked: this visit only */
  }
}

// Your crime records are yours: kept in this browser until you delete them (“Forget what’s kept here”); signing out
// deletes nothing (owner, 2026-10-30).
function forgetCrimeRecords(): void {
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith('omni-torn.crimes.')) localStorage.removeItem(k);
  } catch {
    /* blocked */
  }
}

const pct = (x: number | null) => (x === null ? '—' : `${(x * 100).toFixed(1)}%`);
const perNerve = (x: number | null) => (x === null ? '—' : money(x));
const tile = (label: string, value: string, data = '') => h('div', { class: 'tile', ...(data ? { 'data-tile': data } : {}) }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value' }, value));

export async function renderMyCrimes(view: HTMLElement): Promise<void> {
  const me = session.get();
  if (!me) return;
  const pid = me.playerId;
  const q = new URLSearchParams(location.search);
  let tab: Tab = (['all', 'crime', 'log'] as const).find((t) => t === q.get('tab')) ?? 'all';
  let crime = crimeBySlug(q.get('crime')) ?? CRIME_TYPES[0];
  let skill100 = q.get('cs100') === '1';

  const kept: Kept = load(keptKey(pid), { stats: {}, lite: null, liteAt: 0 });
  const refd: Ref = load(REF_KEY, { at: 0, catalog: [], subs: {}, logCat: null, logCatAt: 0 });
  let values: Map<number, number | null> = new Map();
  let names: Map<number, string> = new Map();
  const status = h('p', { class: 'muted small', 'data-crimes-status': '' });
  const body = h('div', {});
  const tabBar = tabs(
    [
      { id: 'all', label: 'All crimes' },
      { id: 'crime', label: 'One crime' },
      { id: 'log', label: 'From your log' },
    ],
    tab,
    (id) => {
      tab = id as Tab;
      setAddressQuery(null, { tab: tab === 'all' ? null : tab, crime: tab === 'crime' ? crime.slug : null });
      draw();
    },
  );
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Your crimes'), h('p', { class: 'muted' }, ownHistoryOn() ? 'Every crime’s skill, nerve, outcomes, money and uniques — straight from Torn with your key. Your log is kept for you on Omni-Torn; the crime records stay in this browser.' : 'Every crime’s skill, nerve, outcomes, money and uniques — straight from Torn with your key. What it reads stays in this browser.')),
    tabBar,
    status,
    body,
  );

  // ------------------------------------------------------------ reading

  const rows = (): CrimeRow[] => crimeRows(kept.stats, kept.lite, refd.catalog, values);
  const itemName = (id: number) => names.get(id) ?? `Item ${id}`;

  async function readLite(): Promise<void> {
    if (kept.lite && Date.now() - kept.liteAt < 10 * 60_000) return;
    const lite = readCrimesLite(await torn('/v2/user/personalstats', { cat: 'crimes' }, 10 * 60_000));
    if (lite) {
      kept.lite = lite;
      kept.liteAt = Date.now();
      save(keptKey(pid), kept);
    }
  }
  async function readCatalog(): Promise<void> {
    if (refd.catalog.length && Date.now() - refd.at < DAY) return;
    const cat = readCrimeCatalog(await torn('/v2/torn/crimes', {}, DAY));
    if (cat.length) {
      refd.catalog = cat;
      refd.at = Date.now();
      save(REF_KEY, refd);
    }
  }
  async function readSubs(typeId: number): Promise<void> {
    if (refd.subs[typeId]?.length) return;
    const subs = readSubcrimes(await torn(`/v2/torn/${typeId}/subcrimes`, {}, DAY));
    if (subs.length) {
      refd.subs[typeId] = subs;
      save(REF_KEY, refd);
    }
  }
  /** One crime's whole record (kept an hour unless `fresh`). */
  async function readOne(typeId: number, fresh = false): Promise<void> {
    const old = kept.stats[typeId];
    if (!fresh && old && Date.now() - old.at < HOUR) return;
    const s = readUserCrime(await torn(`/v2/user/${typeId}/crimes`, {}, fresh ? 0 : HOUR), typeId, Date.now());
    if (s) {
      kept.stats[typeId] = s;
      save(keptKey(pid), kept);
    }
  }
  /** Every crime not read in the last hour, one at a time, leaving the other pages their share of the minute. */
  async function readAll(fresh = false): Promise<void> {
    for (const t of CRIME_TYPES) {
      const old = kept.stats[t.id];
      if (!fresh && old && Date.now() - old.at < HOUR) continue;
      if (tornCallsLeft() <= SPARE) break;
      await readOne(t.id, fresh);
      if (tab === 'all') draw();
    }
  }
  const unread = () => CRIME_TYPES.filter((t) => !kept.stats[t.id] || Date.now() - kept.stats[t.id]!.at >= HOUR).length;

  const setStatus = () => {
    const n = unread();
    const oldest = Math.min(...Object.values(kept.stats).map((s) => s.at));
    const again = h('button', { type: 'button', class: 'btn-small', 'data-action': 'read-all' }, n ? `Read the other ${n}` : 'Read all again');
    again.addEventListener('click', async () => {
      again.disabled = true;
      status.replaceChildren('Reading…');
      try {
        await readAll(n === 0);
      } catch (err) {
        status.replaceChildren(problem(err));
        return;
      }
      setStatus();
      draw();
    });
    status.replaceChildren(
      Object.keys(kept.stats).length ? `Records read ${Number.isFinite(oldest) ? ago(oldest) : ''}${n ? ` · ${n} of ${CRIME_TYPES.length} not read yet (one Torn call each, leaving your other pages their share)` : ''}. ` : 'Reading your crimes… ',
      again,
    );
  };

  // ------------------------------------------------------------ All crimes

  function drawAll(): HTMLElement {
    const all = rows();
    const shown = skill100 ? all.filter((r) => (r.skill ?? 0) >= MAX_CRIME_SKILL) : all;
    const t = crimeTotals(shown);
    const cs = h('label', { class: 'check' }, h('input', { type: 'checkbox', 'data-filter': 'cs100', ...(skill100 ? { checked: true } : {}) }), ' Skill 100 only');
    cs.querySelector('input')!.addEventListener('change', (e) => {
      skill100 = (e.target as HTMLInputElement).checked;
      setAddressQuery(null, { cs100: skill100 ? '1' : null });
      draw();
    });
    const link = (r: CrimeRow) => {
      const a = h('a', { href: `?tab=crime&crime=${r.type.slug}`, 'data-crime-link': r.type.slug }, r.type.name);
      a.addEventListener('click', (e) => {
        e.preventDefault();
        crime = r.type;
        pick('crime');
      });
      return a;
    };
    return h(
      'div',
      {},
      h(
        'div',
        { class: 'tiles' },
        tile('At skill 100', `${t.maxed} of ${CRIME_TYPES.length}`, 'maxed'),
        tile('Nerve spent', num(t.nerve), 'nerve'),
        tile('Money', short(t.money)),
        tile('Items (market value)', short(t.itemsValue)),
        tile('Per nerve', perNerve(t.perNerve), 'per-nerve'),
        tile('Uniques found', t.uniquesTotal !== null ? `${num(t.uniquesFound)} / ${num(t.uniquesTotal)}` : num(t.uniquesFound), 'uniques'),
      ),
      box(
        'Every crime',
        [
          h('div', { class: 'toolbar' }, cs),
          table(
            shown,
            [
              { label: 'Crime', get: link, sort: (r) => r.type.id },
              { label: 'Skill', get: (r) => (r.skill === null ? '—' : num(r.skill, 2)), sort: (r) => r.skill ?? -1, cls: 'r' },
              { label: 'Nerve', get: (r) => num(r.nerve), sort: (r) => r.nerve ?? -1, cls: 'r' },
              { label: 'Attempts', get: (r) => num(r.attempts), sort: (r) => r.attempts ?? -1, cls: 'r' },
              { label: 'Success', get: (r) => pct(r.successRate), sort: (r) => r.successRate ?? -1, cls: 'r' },
              { label: 'Critical fails', get: (r) => num(r.critical), sort: (r) => r.critical ?? -1, cls: 'r' },
              { label: 'Money', get: (r) => (r.money === null ? '—' : short(r.money)), sort: (r) => r.money ?? -1, cls: 'r' },
              { label: 'Items', get: (r) => (r.itemsValue === null ? '—' : short(r.itemsValue)), sort: (r) => r.itemsValue ?? -1, cls: 'r' },
              { label: 'Per nerve', get: (r) => perNerve(r.perNerve), sort: (r) => r.perNerve ?? -1, cls: 'r' },
              { label: 'Uniques', get: (r) => (r.uniques.found === null ? '—' : `${r.uniques.found}${r.uniques.total !== null ? ` / ${r.uniques.total}` : ''}`), sort: (r) => (r.uniques.total ? (r.uniques.found ?? 0) / r.uniques.total : -1), cls: 'r' },
            ],
            { initial: 0, desc: false, remember: 'mycrimes-all', empty: skill100 ? 'No crime at skill 100 yet.' : 'Nothing read yet.' },
          ),
          h('p', { class: 'muted small' }, 'Items are counted at Torn’s market value. Skills come from your personal stats; the rest from each crime’s record. Tools and materials used up aren’t in Torn’s crime records — the log tab counts them where your log names them.'),
        ],
        { id: 'crimes-all' },
      ),
    );
  }

  // ------------------------------------------------------------ One crime

  function drawCrime(): HTMLElement {
    const sel = filterSelect('Crime', CRIME_TYPES.map((c) => [c.slug, c.name] as const), crime.slug, (v) => {
      crime = crimeBySlug(v) ?? crime;
      setAddressQuery(null, { crime: crime.slug });
      draw();
    }, 'crime');
    const s = kept.stats[crime.id] ?? null;
    const info = refd.catalog.find((c) => c.id === crime.id) ?? null;
    const row = rows().find((r) => r.type.id === crime.id)!;
    const inner = h('div', {}, loading('Reading this crime…'));
    const fill = () => {
      const s2 = kept.stats[crime.id] ?? null;
      if (!s2) return inner.replaceChildren(h('p', { class: 'muted' }, 'Not read yet.'));
      const subs = refd.subs[crime.id] ?? [];
      const subName = (id: number) => subs.find((x) => x.id === id)?.name ?? `Sub-crime ${id}`;
      const missing = uniquesMissing(s2, info);
      inner.replaceChildren(
        ...kids(
          h(
            'div',
            { class: 'cards two' },
            box('Record', facts([
              ['Skill', row.skill === null ? '—' : num(row.skill, 2)],
              ['Progression bonus', s2.progressionBonus === null ? '—' : `${num(s2.progressionBonus)}%`],
              ['Nerve spent', num(s2.nerveSpent)],
              ['Attempts', `${num(s2.attempts.total)} — ${num(s2.attempts.success)} success, ${num(s2.attempts.fail)} fail, ${num(s2.attempts.critical)} critical`],
              ['Success rate', pct(row.successRate)],
              ['Money', money(s2.money)],
              ['Items (market value)', money(row.itemsValue)],
              ['Ammo', s2.ammo.standard || s2.ammo.special ? `${num(s2.ammo.standard)} standard, ${num(s2.ammo.special)} special` : '—'],
              ['Per nerve', perNerve(row.perNerve)],
              ['Read', ago(s2.at)],
            ]), { id: 'crime-record' }),
            box(
              `Uniques — ${s2.uniques.length}${info?.uniqueTotal ? ` of ${info.uniqueTotal}` : ''} found`,
              [
                info?.uniqueTotal ? h('p', {}, s2.uniques.length >= info.uniqueTotal ? h('strong', {}, 'Every unique found.') : `${info.uniqueTotal - s2.uniques.length} still to find${missing.length ? ` (Torn’s numbers: ${missing.join(', ')})` : ''}.`) : null,
                s2.uniques.length
                  ? table(s2.uniques, [
                      { label: '#', get: (u) => String(u.id), sort: (u) => u.id },
                      { label: 'Reward', get: (u) => [u.money ? `${money(u.money.min)}${u.money.max !== u.money.min ? `–${money(u.money.max)}` : ''}` : '', ...u.items.map((i) => `${i.amount}× ${itemName(i.id)}`), u.ammo].filter(Boolean).join(', ') || '—' },
                    ], { initial: 0, desc: false })
                  : h('p', { class: 'muted' }, 'None found yet.'),
                h('p', { class: 'muted small' }, 'Torn’s API names uniques by number only, never by what happens.'),
              ],
              { id: 'crime-uniques' },
            ),
          ),
          box('Sub-crimes', table(s2.subcrimes, [
            { label: 'Sub-crime', get: (x) => subName(x.id), sort: (x) => x.id },
            { label: 'Nerve', get: (x) => num(subs.find((y) => y.id === x.id)?.nerve ?? null), cls: 'r' },
            { label: 'Attempts', get: (x) => num(x.total), sort: (x) => x.total, cls: 'r' },
            { label: 'Success', get: (x) => num(x.success), sort: (x) => x.success, cls: 'r' },
            { label: 'Fail', get: (x) => num(x.fail), sort: (x) => x.fail, cls: 'r' },
            { label: 'Rate', get: (x) => pct(x.total ? x.success / x.total : null), sort: (x) => (x.total ? x.success / x.total : -1), cls: 'r' },
          ], { initial: 0, desc: false, empty: 'No attempts yet.' }), { id: 'crime-subs' }),
          box('Items it gave', table(s2.items, [
            { label: 'Item', get: (i) => itemName(i.id), sort: (i) => itemName(i.id) },
            { label: 'Amount', get: (i) => num(i.amount), sort: (i) => i.amount, cls: 'r' },
            { label: 'Value', get: (i) => money(i.amount * (values.get(i.id) ?? 0)), sort: (i) => i.amount * (values.get(i.id) ?? 0), cls: 'r' },
          ], { initial: 2, empty: 'No items yet.' }), { id: 'crime-items' }),
          Object.keys(s2.misc).length ? box('Its own counters', facts(Object.entries(s2.misc).map(([k, v]): [string, string] => [k.replace(/_/g, ' '), num(v)]))) : null,
        ),
      );
    };
    void (async () => {
      try {
        await Promise.all([readOne(crime.id).then(fill), readSubs(crime.id).catch(() => undefined)]);
      } catch (err) {
        if (!kept.stats[crime.id]) return inner.replaceChildren(problem(err));
      }
      fill();
    })();
    if (s) fill();
    return h('div', {}, h('div', { class: 'toolbar' }, sel), inner);
  }

  // ------------------------------------------------------------ From your log (the log kit)

  let store: LogStore | null = null;
  let reader: LogReader | null = null;
  let serverMerged = false;
  let logRange: DateRange = rangeFromAddress('30d');
  let logCrime = q.get('lcrime') ?? '';
  let logOutcome = q.get('outcome') ?? '';
  let measure: CrimeMeasure = (Object.keys(CRIME_MEASURES) as CrimeMeasure[]).find((m) => m === q.get('measure')) ?? 'attempts';
  const logNote = h('span', { class: 'muted small', 'data-log-note': '' });

  async function ensureReader(): Promise<LogReader> {
    if (reader) return reader;
    store = await logStore(pid);
    if (refd.logCat === null || Date.now() - refd.logCatAt > 7 * DAY) {
      try {
        refd.logCat = findLogCategory(await torn('/v2/torn/logcategories', {}, DAY), /^crimes?$/i);
        refd.logCatAt = Date.now();
        save(REF_KEY, refd);
      } catch {
        /* without it: the whole log, crimes picked out here */
      }
    }
    const stream: LogStream = refd.logCat ? { id: `cat:${refd.logCat}`, cat: refd.logCat } : { id: 'all' };
    reader = new LogReader(store, stream);
    return reader;
  }

  function drawLog(): HTMLElement {
    const out = h('div', {}, loading(readingLogText()));
    void (async () => {
      try {
        const r = await ensureReader();
        if (!serverMerged) {
          serverMerged = true;
          await firstRead(r).then(
            (t) => void (logNote.textContent = t),
            (err: unknown) => logNote.replaceChildren(problem(err)),
          );
        }
        await fillLog(out);
      } catch (err) {
        out.replaceChildren(problem(err));
      }
    })();
    return out;
  }

  async function fillLog(out: HTMLElement): Promise<void> {
    const r = await ensureReader();
    const idx = crimeNameIndex(refd.subs, refd.catalog);
    const raw = await store!.range({ from: null, to: null });
    const all = raw.map((l) => readCrimeLogEntry(l, idx)).filter((e): e is CrimeLogEntry => !!e);
    const maxed = new Set<number>(rows().filter((x) => (x.skill ?? 0) >= MAX_CRIME_SKILL).map((x) => x.type.id));
    const keyOf = (e: CrimeLogEntry) => (e.typeId !== null ? String(e.typeId) : `name:${looseName(e.crime) || 'unknown'}`);
    const inPick = all.filter((e) => inRange(e.at, logRange));
    const shown = inPick.filter(
      (e) =>
        (!logCrime || keyOf(e) === logCrime) &&
        (!logOutcome || (logOutcome === 'jailed' ? e.jailed : logOutcome === 'unique' ? e.unique : e.outcome === logOutcome)) &&
        (!skill100 || (e.typeId !== null && maxed.has(e.typeId))),
    );
    const sums = summariseCrimeLog(shown, values);
    const crimeOpts: Array<readonly [string, string]> = [['', 'Every crime'], ...CRIME_TYPES.map((c) => [String(c.id), c.name] as const)];
    for (const e of all) if (e.typeId === null && !crimeOpts.some(([k]) => k === keyOf(e))) crimeOpts.push([keyOf(e), e.crime || 'Not named in the log']);
    const reread = () => void fillLog(out);
    const setQ = () => setAddressQuery(logRange, { lcrime: logCrime || null, outcome: logOutcome || null, measure: measure === 'attempts' ? null : measure, cs100: skill100 ? '1' : null });
    const picker = rangePicker(logRange, (nr) => {
      logRange = nr;
      setQ();
      reread();
    });
    const cs = h('label', { class: 'check' }, h('input', { type: 'checkbox', 'data-filter': 'cs100', ...(skill100 ? { checked: true } : {}) }), ' Skill 100 only');
    cs.querySelector('input')!.addEventListener('change', (e) => {
      skill100 = (e.target as HTMLInputElement).checked;
      setQ();
      reread();
    });
    const filters = h(
      'div',
      { class: 'toolbar' },
      filterSelect('Crime', crimeOpts, logCrime, (v) => ((logCrime = v), setQ(), reread()), 'log-crime'),
      filterSelect('Outcome', [['', 'Any outcome'], ['success', 'Success'], ['fail', 'Fail'], ['critical', 'Critical fail'], ['jailed', 'Jailed'], ['unique', 'Unique']], logOutcome, (v) => ((logOutcome = v), setQ(), reread()), 'outcome'),
      cs,
      h('span', { class: 'muted small', 'data-log-count': '' }, `${num(shown.length)} of ${num(all.length)} crime lines kept`),
    );
    const chart = zoomChart(sumPerDay(shown, (e) => CRIME_MEASURES[measure].of(e, values), logRange, Date.now()), {
      label: CRIME_MEASURES[measure].label,
      format: measure === 'money' || measure === 'profit' ? (n) => `$${short(n)}` : short,
      onZoom: (nr) => {
        logRange = nr;
        setQ();
        reread();
      },
    });
    const measureSel = filterSelect('Chart shows', (Object.keys(CRIME_MEASURES) as CrimeMeasure[]).map((m) => [m, CRIME_MEASURES[m].label] as const), measure, (v) => ((measure = v as CrimeMeasure), setQ(), reread()), 'measure');

    const kept = await keptLogPanel({
      reader: r,
      store: store!,
      range: logRange,
      matching: all.length,
      what: 'crimes',
      note: logNote,
      forgetText: forgetLogText(' and crime records'),
      onForget: () => {
        forgetCrimeRecords();
        reader = null;
        store = null;
      },
      redraw: reread,
    });
    out.replaceChildren(
      kept,
      picker,
      filters,
      h(
        'div',
        { class: 'tiles' },
        tile('Attempts', num(sums.all.attempts), 'log-attempts'),
        tile('Success', pct(sums.all.attempts ? sums.all.success / sums.all.attempts : null)),
        tile('Jailed', num(sums.all.jailed)),
        tile('Nerve', num(sums.all.nerve)),
        tile('Profit', money(sums.all.profit), 'log-profit'),
        tile('Per nerve', perNerve(sums.all.perNerve)),
      ),
      box(h('h2', {}, `${CRIME_MEASURES[measure].label} per day`), chart, { id: 'log-chart', extra: measureSel }),
      box(
        'Per crime',
        table(sums.rows, [
          { label: 'Crime', get: (x) => x.name, sort: (x) => x.typeId ?? 99 },
          { label: 'Attempts', get: (x) => num(x.attempts), sort: (x) => x.attempts, cls: 'r' },
          { label: 'Success', get: (x) => num(x.success), sort: (x) => x.success, cls: 'r' },
          { label: 'Fail', get: (x) => num(x.fail), sort: (x) => x.fail, cls: 'r' },
          { label: 'Critical', get: (x) => num(x.critical), sort: (x) => x.critical, cls: 'r' },
          { label: 'Jailed', get: (x) => num(x.jailed), sort: (x) => x.jailed, cls: 'r' },
          { label: 'Nerve', get: (x) => num(x.nerve), sort: (x) => x.nerve, cls: 'r' },
          { label: 'Money', get: (x) => short(x.money), sort: (x) => x.money, cls: 'r' },
          { label: 'Items', get: (x) => short(x.itemsValue), sort: (x) => x.itemsValue, cls: 'r' },
          { label: 'Costs', get: (x) => short(x.costs), sort: (x) => x.costs, cls: 'r' },
          { label: 'Profit', get: (x) => short(x.profit), sort: (x) => x.profit, cls: 'r' },
          { label: 'Per nerve', get: (x) => perNerve(x.perNerve), sort: (x) => x.perNerve ?? -Infinity, cls: 'r' },
        ], { initial: 0, desc: false, empty: 'No crimes in this range.' }),
        { id: 'log-per-crime' },
      ),
      box(
        'Log lines',
        [
          table(shown.slice(0, 500), [
            { label: 'When', get: (e) => h('span', { title: new Date(e.at).toUTCString() }, tct(e.at)), sort: (e) => e.at },
            { label: 'Crime', get: (e) => e.crime || '—', sort: (e) => e.crime },
            { label: 'Outcome', get: (e) => `${e.outcome}${e.jailed ? ', jailed' : ''}${e.unique ? ', unique' : ''}`, sort: (e) => e.outcome },
            { label: 'Nerve', get: (e) => num(e.nerve), sort: (e) => e.nerve, cls: 'r' },
            { label: 'Money', get: (e) => (e.money ? money(e.money) : '—'), sort: (e) => e.money, cls: 'r' },
            { label: 'Items', get: (e) => [...e.gained.map((i) => `+${i.amount}× ${itemName(i.id)}`), ...e.lost.map((i) => `−${i.amount}× ${itemName(i.id)}`)].join(', ') || '—' },
            { label: 'Value', get: (e) => short(itemsValue(e.gained, values) - itemsValue(e.lost, values)), sort: (e) => itemsValue(e.gained, values) - itemsValue(e.lost, values), cls: 'r' },
          ], { csv: false, initial: 0, empty: 'Nothing matches.' }),
          shown.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(shown.length)} — the CSV has them all.`) : null,
        ].filter(Boolean) as HTMLElement[],
        { id: 'log-lines', extra: csvButton(() => `crimes-${rangeText(logRange).replace(/[^\w]+/g, '-').toLowerCase()}.csv`, () => crimeLogCsvRows(shown)) },
      ),
      h('p', { class: 'muted small' }, 'Read from your log as Torn writes it: a crime line names the crime (or the place within it), the outcome, the nerve and what it gave or used up. Lines whose crime Omni-Torn can’t place are listed by their own name.'),
    );
  }

  // ------------------------------------------------------------ drawing

  const pick = (t: Tab) => {
    tab = t;
    setAddressQuery(null, { tab: t === 'all' ? null : t, crime: t === 'crime' ? crime.slug : null });
    for (const b of tabBar.querySelectorAll('button')) {
      const on = b.textContent === { all: 'All crimes', crime: 'One crime', log: 'From your log' }[t];
      b.classList.toggle('active', on);
      b.setAttribute('aria-selected', String(on));
    }
    draw();
  };

  function draw(): void {
    if (tab === 'all') body.replaceChildren(drawAll());
    else if (tab === 'crime') body.replaceChildren(drawCrime());
    else body.replaceChildren(gated('From your log', { level: 4, user: ['log'] }, 'your log reads crimes day by day, with what they cost.', drawLog));
  }

  // First what's kept (no call), then Torn.
  draw();
  setStatus();
  try {
    const [items] = await Promise.all([ref('items').catch(() => null), readLite().catch(() => undefined), readCatalog().catch(() => undefined)]);
    if (items) {
      values = new Map(items.data.map((i) => [i.id, i.marketValue]));
      names = new Map(items.data.map((i) => [i.id, i.name]));
    }
    if (tab !== 'log') draw();
    await readAll();
  } catch (err) {
    status.replaceChildren(problem(err));
    return;
  }
  setStatus();
  if (tab === 'all') draw();
}
